import React, { useState, useRef } from 'react';
import { Database, Upload, CheckCircle, XCircle, AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = window.location.origin;

const RestoreBackupModal = ({ isOpen, onClose, onSuccess }) => {
  const [restoring, setRestoring] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [options, setOptions] = useState({
    restore_users: true,
    restore_books: true,
    restore_purchases: true,
    restore_pending: true
  });
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        setError('Please select a JSON backup file');
        return;
      }
      setSelectedFile(file);
      setError(null);
      setResults(null);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) {
      setError('Please select a backup file first');
      return;
    }

    setRestoring(true);
    setError(null);

    try {
      const text = await selectedFile.text();
      const backupData = JSON.parse(text);

      // Validate backup format - support multiple structures
      // Just check if any of the container keys exist
      const hasData = backupData.data && typeof backupData.data === 'object';
      const hasCollections = backupData.collections && typeof backupData.collections === 'object';
      const hasRootData = backupData.users || backupData.books;
      
      if (!hasData && !hasCollections && !hasRootData) {
        setError(`Invalid backup file. Found keys: ${Object.keys(backupData).join(', ')}. Expected: "data", "collections", or "users"/"books"`);
        setRestoring(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/admin/backup/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          backup_data: backupData,
          restore_options: options
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResults(data);
        if (onSuccess) onSuccess();
      } else {
        setError(data.detail || 'Restore failed');
      }
    } catch (err) {
      setError(`Failed to process backup: ${err.message}`);
    } finally {
      setRestoring(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResults(null);
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-gray-800 rounded-2xl p-6 max-w-lg w-full border border-gray-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold text-white">Restore Database</h3>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        {restoring ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">Restoring database...</p>
            <p className="text-gray-400 text-sm mt-2">This may take a moment</p>
          </div>
        ) : results ? (
          <div>
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-white">Restore Complete</h4>
            </div>
            
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-400">{results.summary.total_imported}</p>
                <p className="text-xs text-gray-400">Imported</p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-yellow-400">{results.summary.total_skipped}</p>
                <p className="text-xs text-gray-400">Skipped</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-400">{results.summary.total_errors}</p>
                <p className="text-xs text-gray-400">Errors</p>
              </div>
            </div>

            {/* Detailed breakdown */}
            <div className="bg-gray-900/50 rounded-lg p-4 mb-6 text-sm">
              <p className="text-gray-400 mb-2">Details:</p>
              <div className="space-y-1 text-gray-300">
                <p>Users: {results.details.users.imported} imported, {results.details.users.skipped} skipped</p>
                <p>Books: {results.details.books.imported} imported, {results.details.books.skipped} skipped</p>
                <p>Purchases: {results.details.purchases.imported} imported, {results.details.purchases.skipped} skipped</p>
                <p>Pending: {results.details.pending_purchases.imported} imported, {results.details.pending_purchases.skipped} skipped</p>
              </div>
            </div>

            <Button onClick={handleClose} className="w-full bg-purple-600 hover:bg-purple-700">
              Done
            </Button>
          </div>
        ) : (
          <div>
            {/* Warning */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-yellow-400 font-medium">Important</p>
                  <p className="text-gray-400 mt-1">
                    This will import data from a previously exported backup. Existing records with the same IDs will be skipped (not overwritten).
                  </p>
                </div>
              </div>
            </div>

            {/* File Input */}
            <div className="mb-6">
              <label className="block text-gray-300 mb-2 text-sm">Select Backup File (.json)</label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".json"
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-purple-500 transition-colors"
              >
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                {selectedFile ? (
                  <p className="text-purple-400 font-medium">{selectedFile.name}</p>
                ) : (
                  <p className="text-gray-400">Click to select backup file</p>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="mb-6">
              <p className="text-gray-300 text-sm mb-3">What to restore:</p>
              <div className="space-y-2">
                {[
                  { key: 'restore_users', label: 'Users' },
                  { key: 'restore_books', label: 'Books' },
                  { key: 'restore_purchases', label: 'Purchases' },
                  { key: 'restore_pending', label: 'Pending Purchases' }
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={options[key]}
                      onChange={(e) => setOptions({ ...options, [key]: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-gray-300 text-sm">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleClose} className="flex-1 border-gray-600 text-gray-300">
                Cancel
              </Button>
              <Button
                onClick={handleRestore}
                disabled={!selectedFile}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
              >
                <Database className="w-4 h-4 mr-2" />
                Restore
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RestoreBackupModal;
